import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Modal, Form, Input, Select, Switch, 
  message, Popconfirm, Tag, Card, Row, Col, Statistic 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, 
  CrownOutlined, ShopOutlined, PhoneOutlined, HomeOutlined, GlobalOutlined,
  LoginOutlined
} from '@ant-design/icons';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import countryList from 'react-select-country-list';
import { userAPI } from '../api';

const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [phoneValue, setPhoneValue] = useState();
  const [countries, setCountries] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个用户`,
  });
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    dropshippers: 0,
    active: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // 初始化国家列表
    const options = countryList().getData();
    setCountries(options);
    
    fetchUsers();
  }, []);

  const fetchUsers = async (page = pagination.current, pageSize = pagination.pageSize, searchValue = searchTerm) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize,
      };
      const trimmedSearch = (searchValue || '').trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      const response = await userAPI.getAll(params);
      const responseData = response.data || {};
      setUsers(responseData.data || []);
      setPagination(prev => ({
        ...prev,
        current: responseData.page || page,
        pageSize: responseData.page_size || pageSize,
        total: responseData.total || 0,
      }));
      const summary = responseData.summary || {};
      setStats({
        total: responseData.total || 0,
        admins: summary.admin_count || 0,
        dropshippers: summary.dropshipper_count || 0,
        active: summary.active_count || 0,
      });
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setPhoneValue(undefined);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      username: record.username,
      email: record.email,
      role: record.role,
      is_active: record.is_active,
      name: record.name,
      company_name: record.company_name,
      country: record.country,
      address: record.address
    });
    setPhoneValue(record.phone);
    setModalVisible(true);
  };

  const handleDelete = async (id, username) => {
    try {
      await userAPI.delete(id);
      message.success(`用户 ${username} 删除成功`);
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data.detail);
      } else {
        message.error('删除失败');
      }
    }
  };

  const handleImpersonate = async (record) => {
    try {
      const response = await userAPI.impersonate(record.id);
      const { access_token: accessToken } = response.data || {};
      if (!accessToken) {
        message.error('登录失败，未获取到令牌');
        return;
      }
      localStorage.setItem('token', accessToken);
      message.success(`已切换为 ${record.username}`);
      window.location.href = '/dropshipper';
    } catch (error) {
      message.error(error.response?.data?.detail || '登录失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        phone: phoneValue || null
      };
      
      if (editingUser) {
        // 编辑时，如果密码为空则不更新密码
        if (!submitData.password) {
          delete submitData.password;
        }
        await userAPI.update(editingUser.id, submitData);
        message.success('用户更新成功');
      } else {
        await userAPI.create(submitData);
        message.success('用户创建成功');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data.detail);
      } else {
        message.error(editingUser ? '更新失败' : '创建失败');
      }
    }
  };

  const getRoleTag = (role) => {
    const roleConfig = {
      admin: { color: 'red', icon: <CrownOutlined />, text: 'Admin' },
      dropshipper: { color: 'blue', icon: <ShopOutlined />, text: 'Dropshipper' }
    };
    
    const config = roleConfig[role] || { color: 'default', icon: <UserOutlined />, text: role };
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    setPagination(prev => ({
      ...prev,
      current,
      pageSize,
    }));
    fetchUsers(current, pageSize, searchTerm);
  };

  const handleSearchUsers = (value) => {
    const normalized = (value || '').trim();
    setSearchTerm(normalized);
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
    fetchUsers(1, pagination.pageSize, normalized);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '8%',
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: '20%',
      ellipsis: true,
      render: (username, record) => (
        <div>
          <strong>{username}</strong>
          {!record.is_active && (
            <Tag color="red" size="small" style={{ marginLeft: 8 }}>
              已禁用
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: '23%',
      ellipsis: true,
      render: (name) => name || <span style={{ color: '#999' }}>未设置</span>,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: '15%',
      render: (role) => getRoleTag(role),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'Dropshipper', value: 'dropshipper' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Switch checked={isActive} disabled size="small" />
      ),
      width: '8%',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '17%',
    },
    {
      title: '操作',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Popconfirm
            title={`确定以 "${record.username}" 身份登录吗？`}
            onConfirm={() => handleImpersonate(record)}
            okText="确认"
            cancelText="取消"
            disabled={!record.is_active}
          >
            <Button
              size="small"
              icon={<LoginOutlined />}
              disabled={!record.is_active}
            >
              登录
            </Button>
          </Popconfirm>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title={`确定要删除用户 "${record.username}" 吗？`}
            onConfirm={() => handleDelete(record.id, record.username)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              disabled={record.role === 'admin' && stats.admins <= 1}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="用户总数" 
              value={stats.total} 
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Admin" 
              value={stats.admins} 
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Dropshipper" 
              value={stats.dropshippers} 
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="活跃用户" 
              value={stats.active} 
              suffix={`/ ${stats.total}`}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 页面标题和操作按钮 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>用户管理</h1>
          <Input.Search
            placeholder="搜索用户名 / 邮箱 / 姓名 / 公司"
            allowClear
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (!value) {
                handleSearchUsers('');
              }
            }}
            onSearch={handleSearchUsers}
            style={{ minWidth: 260 }}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加用户
        </Button>
      </div>

      {/* 用户表格 */}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="middle"
      />

      {/* 添加/编辑用户Modal */}
      <Modal
        title={editingUser ? '编辑用户' : '添加用户'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="确认"
        cancelText="取消"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="用户名"
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少需要3个字符' },
              { max: 50, message: '用户名不能超过50个字符' },
              { pattern: /^[A-Za-z0-9]+$/, message: '用户名只能包含大小写字母和数字' },
            ]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="请输入邮箱（可选）" />
          </Form.Item>

          <Form.Item
            label="密码"
            name="password"
            rules={editingUser ? [] : [
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少需要6个字符' },
            ]}
            extra={editingUser ? "留空保持当前密码" : ""}
          >
            <Input.Password placeholder={editingUser ? "请输入新密码（可选）" : "请输入密码"} />
          </Form.Item>

          <Form.Item
            label="姓名"
            name="name"
          >
            <Input prefix={<UserOutlined />} placeholder="请输入姓名（可选）" />
          </Form.Item>

          <Form.Item
            label="电话号码"
          >
            <PhoneInput
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              value={phoneValue}
              onChange={setPhoneValue}
              style={{
                padding: '4px 11px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '14px',
                height: '32px',
                display: 'flex',
                alignItems: 'center'
              }}
            />
          </Form.Item>

          <Form.Item
            label="公司名称"
            name="company_name"
          >
            <Input prefix={<ShopOutlined />} placeholder="请输入公司名称（可选）" />
          </Form.Item>

          <Form.Item
            label="国家"
            name="country"
          >
            <Select
              showSearch
              placeholder="请选择国家（可选）"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              prefix={<GlobalOutlined />}
            >
              <Option value="">请选择国家（可选）</Option>
              {countries.map(country => (
                <Option key={country.value} value={country.value}>
                  {country.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="地址"
            name="address"
          >
            <Input.TextArea 
              prefix={<HomeOutlined />} 
              placeholder="请输入详细地址（可选）"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="角色"
            name="role"
            rules={[
              { required: true, message: '请选择角色' },
            ]}
          >
            <Select placeholder="请选择角色">
              <Option value="admin">
                <CrownOutlined /> Admin
              </Option>
              <Option value="dropshipper">
                <ShopOutlined /> Dropshipper
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="是否启用"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
